import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  BreadcrumbGroup,
  ContentLayout,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { Event, Shift } from '@amzn/red-velvet-api';
import { SignupTableShiftHeader } from './signupTableShiftHeader';
import { SignupTableForShift } from './signupTableForShift';
import { useAllSignupsByShiftId } from '../../hooks/signup/useSignupsByShiftId';
import { useShiftFromUrlParams } from '../../hooks/shifts/shiftFromUrlParams';
import { getValueFromSearchParam } from '../../utils/url';
import { isUnsupportedSignup } from '../../utils/signupUtils';
import { UnsupportedSignupLayout } from './CoordinatorManageSignupsPageCommon';

interface LocationState {
  event?: Event;
  shift?: Shift;
}

interface CoordinatorManageSignupsPageForShiftProps {
  navigation: React.ReactNode;
}

export function CoordinatorManageSignupsPageForShift({
  navigation,
}: CoordinatorManageSignupsPageForShiftProps) {
  const { t } = useTranslation(['signup', 'activity', 'translation']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');

  // Try to get event/shift from route state first
  const locationState = location.state as LocationState | null;
  const eventFromState = locationState?.event;
  const shiftFromState = locationState?.shift;

  // Fallback: fetch from API only if not passed via route state
  const needsFetch = !eventFromState || !shiftFromState;
  const {
    event: eventFromApi,
    shift: shiftFromApi,
    isFetched: isEventShiftCoordinatorFetched,
    isError: isEventShiftCoordinatorFetchedWithError,
  } = useShiftFromUrlParams(needsFetch);

  // Use route state if available, otherwise use API data
  const event = eventFromState || eventFromApi;
  const shift = shiftFromState || shiftFromApi;
  const isEventShiftCoordinatorLoading = needsFetch && !isEventShiftCoordinatorFetched;

  // Fetch signups
  const {
    signups,
    isLoading: isSignupsForShiftLoading,
    isError: isSignupsForShiftError,
  } = useAllSignupsByShiftId(shiftId);

  if (isUnsupportedSignup(event, shift)) {
    return <UnsupportedSignupLayout />;
  }

  return (
    <AppLayout
      footerSelector=".footer"
      navigation={navigation}
      toolsHide={true}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
            {
              text: t('breadcrumbs.coordinatorActivity', { ns: 'activity' }),
              href: '/coordinator-activity',
            },
            { text: shift?.name || '', href: '#' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />
      }
      content={
        <ContentLayout>
          <SpaceBetween direction="vertical" size="l">
            <SignupTableShiftHeader
              event={event}
              shift={shift}
              isLoading={isEventShiftCoordinatorLoading}
              isError={isEventShiftCoordinatorFetchedWithError}
            />
            <SignupTableForShift
              signups={signups}
              isLoading={isSignupsForShiftLoading}
              isError={isSignupsForShiftError}
              shift={shift}
            />
          </SpaceBetween>
        </ContentLayout>
      }
      contentType="table"
    />
  );
}
